import {
  useState,
  useEffect,
  useEffectEvent,
  useCallback,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router';
import type { InvestorFilters } from '@/types/investor';

const DEFAULT_PAGE_SIZE = 25;

interface UseInvestorFiltersOptions {
  defaultPageSize?: number;
}

interface UseInvestorFiltersReturn {
  // Filter state
  filters: InvestorFilters;
  searchInput: string;
  showPendingChangesOnly: boolean;

  // Pagination state
  page: number;
  pageSize: number;

  // Handlers
  handleFilterChange: (
    key: keyof InvestorFilters | 'fund' | 'pending_changes',
    value: string | undefined | boolean
  ) => void;
  clearFilters: () => void;
  handlePageChange: (newPage: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;

  // For resetting selection when filters change
  filterKey: string;
}

export function useInvestorFilters(
  options: UseInvestorFiltersOptions = {}
): UseInvestorFiltersReturn {
  const { defaultPageSize = DEFAULT_PAGE_SIZE } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [filters, setFilters] = useState<InvestorFilters>({
    investor_type:
      (searchParams.get('type') as
        'corporate' | 'family_office' | 'individual' | 'fund_of_funds') ||
      undefined,
    search: searchParams.get('search') || undefined,
    fund: searchParams.get('fund') || undefined,
    has_pending_changes: searchParams.get('pending') === 'true' || undefined,
    include_archived: searchParams.get('archived') === 'true' || undefined,
  });

  // Separate state for immediate search input (for responsive UI)
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Pending changes filter state (synced with filters.has_pending_changes)
  const [showPendingChangesOnly, setShowPendingChangesOnly] = useState(
    searchParams.get('pending') === 'true'
  );

  // Pagination state
  const [page, setPage] = useState(() =>
    Number.parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(() =>
    Number.parseInt(
      searchParams.get('page_size') || String(defaultPageSize),
      10
    )
  );

  // Debounce search input to avoid API calls on every keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchValue = searchInput.trim() || undefined;
      if (searchValue !== filters.search) {
        setFilters((prev) => ({ ...prev, search: searchValue }));
        setPage(1);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput, filters.search]);

  // Key for tracking filter changes (useful for resetting selection)
  const filterKey = useMemo(
    () =>
      `${filters.search ?? ''}-${filters.investor_type ?? ''}-${filters.fund ?? ''}-${filters.has_pending_changes ?? false}-${filters.include_archived ?? false}`,
    [
      filters.search,
      filters.investor_type,
      filters.fund,
      filters.has_pending_changes,
      filters.include_archived,
    ]
  );

  // Use useEffectEvent to avoid setSearchParams in dependency array
  const updateSearchParams = useEffectEvent((params: URLSearchParams) => {
    setSearchParams(params);
  });

  // Update search params when filters or pagination change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.investor_type) params.set('type', filters.investor_type);
    if (filters.search) params.set('search', filters.search);
    if (filters.fund) params.set('fund', filters.fund);
    if (filters.has_pending_changes) params.set('pending', 'true');
    if (filters.include_archived) params.set('archived', 'true');
    if (page > 1) params.set('page', page.toString());
    if (pageSize !== defaultPageSize)
      params.set('page_size', pageSize.toString());

    updateSearchParams(params);
  }, [
    filters.investor_type,
    filters.search,
    filters.fund,
    filters.has_pending_changes,
    filters.include_archived,
    page,
    pageSize,
    defaultPageSize,
  ]);

  // Handlers - memoized to prevent unnecessary child re-renders
  const handleFilterChange = useCallback(
    (
      key: keyof InvestorFilters | 'fund' | 'pending_changes',
      value: string | undefined | boolean
    ) => {
      if (key === 'pending_changes') {
        const boolValue = value as boolean;
        setShowPendingChangesOnly(boolValue);
        // Sync with filters for server-side filtering
        setFilters((prev) => ({
          ...prev,
          has_pending_changes: boolValue || undefined,
        }));
        setPage(1);
      } else if (key === 'search') {
        // Update immediate search input (debounced effect will update filters and reset page)
        setSearchInput((value as string) || '');
      } else {
        setFilters((prev) => ({
          ...prev,
          [key]: value || undefined,
        }));
        setPage(1);
      }
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchInput('');
    setShowPendingChangesOnly(false);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  return {
    filters,
    searchInput,
    showPendingChangesOnly,
    page,
    pageSize,
    handleFilterChange,
    clearFilters,
    handlePageChange,
    handlePageSizeChange,
    filterKey,
  };
}
